import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift } from 'lucide-react';
import { ProductGrid } from '@/components/pos/ProductGrid';
import { ProductSearchBar } from '@/components/pos/ProductSearchBar';
import { VisitDatePicker } from '@/components/pos/VisitDatePicker';
import { BlockedDateBanner } from '@/components/pos/BlockedDateBanner';
import { CartSidebar } from '@/components/pos/CartSidebar';
import { EmptyState } from '@/components/pos/EmptyState';
import { useCartModeGuard } from '@/components/pos/CartModeGuard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useProducts, useVisitDateBlock } from '@/hooks/queries';
import { usePriceSync } from '@/hooks/usePriceSync';
import { isFocCatalogProduct, isMainTicket } from '@/lib/products';
import { useCartStore } from '@/stores/cart.store';
import { useHotkeys } from '@/hooks/useHotkeys';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useCatalogAvailability } from '@/hooks/useCatalogAvailability';
import { hideUnavailableSlotTickets } from '@/lib/slotAvailability';
import { blockedDateMessage } from '@/lib/visitDateErrors';
import { CashierInfo } from '@/pages/SellPage';
import type { Product } from '@/types/product';

/**
 * Free-of-charge (FOC) catalog — step 1 of the FOC flow:
 * Katalog FOC → Dokumen (/foc/document) → Detail → Bayar.
 *
 * Mirrors the regular SellPage, but lists only FOC entry tickets and has no
 * bundles (the BE refuses a bundle in an FOC cart). The cart is put in 'foc'
 * mode; a cart still holding regular tickets asks to be cleared first.
 */
export function FocSellPage() {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');

  const visitDate = useCartStore((s) => s.visitDate);
  const setVisitDate = useCartStore((s) => s.setVisitDate);
  const addProduct = useCartStore((s) => s.addProduct);
  const lineCount = useCartStore((s) => s.lines.length + s.bundleLines.length);
  const clearCart = useCartStore((s) => s.clearCart);
  const { mismatch: modeMismatch, dialog: modeDialog } = useCartModeGuard('foc');

  // Blocked visit dates apply to FOC sales too (the BE 409s them the same way).
  const visitBlock = useVisitDateBlock(visitDate);
  const dateBlocked = visitBlock !== null;

  function rejectIfBlocked(): boolean {
    if (!visitBlock) return false;
    toast.error(blockedDateMessage(visitBlock.reason, visitDate));
    return true;
  }

  const { data, isLoading, isError, refetch } = useProducts();
  // FOC entry tickets only. FOC attractions are attached per visitor on the
  // Details step, like regular ones.
  const products = useMemo(() => (data?.items ?? []).filter(isFocCatalogProduct), [data]);

  const availabilityByProduct = useCatalogAvailability(products, visitDate);
  // Same rule as the regular catalog: a time-slot ticket with nothing bookable
  // that day is hidden (a blocked date keeps every card, locked).
  const sellableProducts = useMemo(
    () => (dateBlocked ? products : hideUnavailableSlotTickets(products, availabilityByProduct)),
    [dateBlocked, products, availabilityByProduct],
  );
  const hiddenCount = products.length - sellableProducts.length;
  const { productPrices } = usePriceSync();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sellableProducts;
    return sellableProducts.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q),
    );
  }, [sellableProducts, query]);

  const toDocument = () => {
    if (lineCount === 0 || modeMismatch || rejectIfBlocked()) return;
    navigate('/foc/document');
  };

  function selectProduct(product: Product) {
    if (modeMismatch || rejectIfBlocked()) return;
    addProduct(product, { unitPrice: productPrices[product.id] });
    toast.success(`${product.name} ditambahkan.`);
  }

  useHotkeys({
    f2: () => searchRef.current?.focus(),
    f4: toDocument,
    'ctrl+l': () => navigate('/orders'),
    'ctrl+n': () => {
      if (lineCount > 0) {
        if (window.confirm('Kosongkan keranjang?')) clearCart();
      }
    },
  });

  // ─── Barcode scanner ─ match SKU among FOC tickets ──────────────────────────
  useBarcodeScanner({
    onScan: (code) => {
      if (rejectIfBlocked()) return;
      const matches = (p: Product) =>
        p.sku?.toLowerCase() === code.toLowerCase() || p.id === code;
      const match = products.find(matches);
      if (!match) {
        // A regular entry ticket is real, just not sold here.
        if ((data?.items ?? []).some((p) => isMainTicket(p) && matches(p))) {
          toast.error('Tiket reguler dijual lewat halaman Jual.');
          return;
        }
        setQuery(code);
        toast.message('Tiket FOC tidak ditemukan untuk barcode tersebut.');
        return;
      }
      const verdict = availabilityByProduct[match.id];
      if (verdict?.state === 'unavailable') {
        toast.error(
          verdict.reason === 'expired'
            ? `${match.name} — waktu tiket sudah lewat untuk tanggal ini.`
            : `${match.name} — stok habis.`,
        );
        return;
      }
      selectProduct(match);
    },
  });

  const noFocProducts = !isLoading && !isError && products.length === 0;

  return (
    <div className="flex h-full">
      {/* Work area */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="space-y-3 border-b border-border bg-cream-50/70 px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <CashierInfo />
            <div className="ml-auto flex flex-1 flex-wrap items-center justify-end gap-3">
              <VisitDatePicker value={visitDate} onChange={setVisitDate} />
              <div className="min-w-[220px] flex-1">
                <ProductSearchBar ref={searchRef} value={query} onChange={setQuery} />
              </div>
            </div>
          </div>

          {visitBlock && <BlockedDateBanner block={visitBlock} visitDate={visitDate} />}

          <div className="flex flex-wrap items-center gap-3">
            <h1 className="flex items-center gap-2 font-display text-base font-bold leading-none tracking-tight text-ink-900">
              <Gift className="h-4 w-4 text-crimson-700" aria-hidden="true" />
              Tiket FOC
            </h1>
            <Badge variant="gold">Gratis · wajib dokumen</Badge>
            <span className="ml-auto text-xs text-ink-400">{filtered.length} tiket</span>
          </div>
        </div>

        <ScrollArea className="flex-1 px-6 py-5">
          {noFocProducts ? (
            <EmptyState
              icon={Gift}
              title="Belum ada tiket FOC"
              description="Tiket FOC diatur di back office sebagai produk bertanda FOC."
            />
          ) : (
            <ProductGrid
              products={filtered}
              isLoading={isLoading}
              isError={isError}
              onSelect={selectProduct}
              onRetry={() => refetch()}
              resolvedPrices={productPrices}
              visitDate={visitDate}
              blocked={dateBlocked}
              allHidden={!query.trim() && sellableProducts.length === 0 && hiddenCount > 0}
            />
          )}
        </ScrollArea>
      </div>

      <div className="hidden w-[400px] shrink-0 pos:block">
        <CartSidebar onCheckout={toDocument} nextHint="Unggah dokumen FOC" />
      </div>

      {modeDialog}
    </div>
  );
}
